import { useEffect, useState } from "react";
import { Bot, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, formatDate } from "@/lib/api";

const FLAGS = [
  ["autopilot", "Otopilot", "Bugünün Planı'ndaki görevleri sormadan başlat (haftalık bütçe sınırına kadar)"],
  ["weekly_etsy", "Haftalık Etsy bakımı", "Her Pazartesi etiket/fiyat yenile + eksik fotoğraf yükle + e-posta özeti"],
  ["sale_tweets", "Satış → X paylaşımı", "Gumroad'da satış olunca otomatik 'yeni sipariş' paylaşımı"],
  ["affiliate_nudge", "Ortak daveti", "Yeni üyelere %35 ortaklık e-postası + Çarşamba davet tweet'i"],
];

export function AutopilotPanel() {
  const [d, setD] = useState(null);
  const [locked, setLocked] = useState(false);
  const load = () => api.get("/autopilot/settings").then((r) => setD(r.data)).catch((e) => { if (e?.response?.status === 402) setLocked(true); });
  useEffect(() => { load(); }, []);
  const save = async (patch) => {
    const next = { ...d, ...patch };
    setD(next);
    try { await api.put("/autopilot/settings", { autopilot: next.autopilot, weekly_budget_usd: Number(next.weekly_budget_usd), weekly_etsy: next.weekly_etsy, sale_tweets: next.sale_tweets, affiliate_nudge: next.affiliate_nudge }); toast.success("Kaydedildi"); } catch (e) { toast.error(formatError(e)); }
  };
  if (locked || !d) return null;
  return (
    <section className="card-surface p-5 space-y-4" data-testid="autopilot-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><div className="font-heading text-lg font-semibold inline-flex items-center gap-2"><Bot className="h-5 w-5 text-cyan-300" /> Tam otomasyon</div><p className="text-xs text-slate-400">Her sabah 09:00'da çalışır. Sen sadece sonuçlara bakarsın.</p></div>
        <div className="text-xs text-slate-400">Bu hafta tahmini harcama: <b className="text-emerald-300" data-testid="autopilot-week-spend">${d.week_spend_usd.toFixed(2)}</b> / ${Number(d.weekly_budget_usd).toFixed(0)}</div>
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {FLAGS.map(([k, label, help]) => (
          <label key={k} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 ${d[k] ? "border-cyan-500/30 bg-cyan-500/5" : "border-white/10"}`} data-testid={`autopilot-flag-${k}`}>
            <input type="checkbox" checked={!!d[k]} onChange={(e) => save({ [k]: e.target.checked })} className="mt-1 accent-cyan-400" data-testid={`autopilot-toggle-${k}`} />
            <span><span className="block text-sm font-semibold">{label}</span><span className="block text-xs text-slate-400">{help}</span>
              {k === "autopilot" && d.autopilot && <span className="mt-2 flex items-center gap-2 text-xs">Haftalık bütçe $<input type="number" min="1" max="500" value={d.weekly_budget_usd} onChange={(e) => setD({ ...d, weekly_budget_usd: e.target.value })} onBlur={() => save({})} data-testid="autopilot-budget" className="w-20 rounded-md border border-white/10 bg-[#0D111A] px-2 py-1" /></span>}
            </span>
          </label>
        ))}
      </div>
      {d.log.length > 0 && <div className="space-y-1 text-xs text-slate-400" data-testid="autopilot-log">{d.log.map((l) => <div key={l.log_id}>• {formatDate(l.created_at)} — {l.text}{l.est_cost ? ` (~$${l.est_cost.toFixed(2)})` : ""}</div>)}</div>}
    </section>
  );
}

export function RepairBanner() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/repair/assets").then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (d?.last?.status !== "running") return undefined; const t = setInterval(load, 6000); return () => clearInterval(t); }, [d?.last?.status]);
  if (!d || (d.total === 0 && d.last?.status !== "running")) return null;
  const m = d.missing;
  const run = async () => { setBusy(true); try { await api.post("/repair/assets"); toast.success("Onarım başladı"); await load(); } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); } };
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm" data-testid="repair-banner">
      <div className="inline-flex items-center gap-2 text-amber-100"><Wrench className="h-4 w-4" />
        {d.last?.status === "running" ? <span>Eksik dosyalar onarılıyor…</span> : <span><b>{d.total} dosya eksik</b> (kapak {m.covers.length} · PDF {m.pdfs.length} · tasarım {m.designs.length} · logo {m.logos.length}). Kapaklar ücretsiz, tasarımlar ~$0.01, onay bekleyen kitap PDF'leri yeniden yazılır.</span>}
      </div>
      {d.last?.status !== "running" && <button onClick={run} disabled={busy} data-testid="repair-run-btn" className="inline-flex items-center gap-1.5 rounded-md bg-amber-400 px-3 py-1.5 text-xs font-semibold text-[#0A0D14] hover:bg-amber-300 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />} Eksik dosyaları onar</button>}
    </div>
  );
}
